import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Typography} from "@mui/material";
import ProcessingTable from "../../../../components/UI/table/customer-detales/processing/ProcessingTable";
import {Canvas} from "@react-three/fiber";
import {Text} from "@react-three/drei";
import ModalProcessing from "./Modals/ModalProcessing";
import DetailCanvas from "./Canvas/DetailCanvas";
import DetailMapControls, {DETAIL_MIN_ZOOM_MULTIPLIER} from "./Canvas/DetailMapControls";
import {DetailViewTransformContext} from "./Canvas/DetailViewTransformContext";
import ModalConfirm from "./Modals/ModalConfirm";
import Layer from "./Canvas/CanvasComponents/Layer";
import SideBar from "./SideBar/SideBar";
import {calcCutPossible} from "../../../../utils/processing/calcCutPossible";
import {orderContourPoints} from "../../../../utils/processing/orderContourPoints";
import PopoverProcessing from "./Canvas/CanvasComponents/PopoverProcessing";
import ModalAddDetail from "../../../../components/UI/table/customer-detales/ModalAddDetail";
import * as THREE from "three";
import {calcNewScale, INIT_SCALE} from "../../../../utils/processing/calcNewScale";
import {calcProcessingFitSize} from "./Canvas/CanvasCalcPositions/calcSidePos";
import {calcValueOfStringWithVar} from "./Canvas/CanvasCalcPositions/calcValueOfStringWithVar";
import {calcPrevQuarterXY} from "./Canvas/CanvasCalcPositions/calcPrevQuarterXY";
import ProcessingEdgesTable from "../../../../components/UI/table/customer-detales/processing/ProcessingEdgesTable";
import ButtonBack from "../../../../components/UI/button/ButtonBack";
import {ReactComponent as PencilIcon} from "../../../../components/Icons/pencil.svg"
import {useErrorBoundary} from 'use-error-boundary'
import PointControllerService from "../../../../API/details/customer/PointController";
import HoleControllerService from "../../../../API/details/customer/HoleControllerService";
import NotchControllerService from "../../../../API/details/customer/NotchController";
import GrooveControllerService from "../../../../API/details/customer/GrooveControllerService";
import EdgesControllerService from "../../../../API/details/customer/EdgesControllerService";
import QuarterControllerService from "../../../../API/details/customer/QuarterControllerService";
import DetailControllerService from "../../../../API/details/customer/DetailControllerService";
import OrderEdgeController from "../../../../API/details/customer/OrderEdgeController";
import HoleControllerServiceOrganization from "../../../../API/details/organization/HoleControllerServiceOrganization";
import VariableControllerService from "../../../../API/details/customer/VariableControllerService";
import VariableControllerServiceOrganization
    from "../../../../API/details/organization/VariableControllerServiceOrganization";
import DetailReserveService from "../../../../API/organization/DetailReserveService";
import PointControllerServiceOrganization from "../../../../API/details/organization/PointControllerOrganization";
import NotchControllerServiceOrganization
    from "../../../../API/details/organization/NotchControllerServiceOrganization";
import GrooveControllerServiceOrganization
    from "../../../../API/details/organization/GrooveControllerServiceOrganization";
import QuarterControllerServiceOrganization
    from "../../../../API/details/organization/QuarterControllerServiceOrganization";
import OrderEdgeControllerOrganization from "../../../../API/details/organization/OrderEdgeControllerOrganization";
import EdgesControllerServiceOrganization
    from "../../../../API/details/organization/EdgesControllerServiceOrganization";
import {EditLocalStoragePagesData} from "../../../../utils/EditLocalStoragePagesData";
import {useNavigate} from "react-router-dom";

const MainDimension = React.memo(({detailData, scale}) => {
    // const {detailData, scale} = useProcessingContext()

    return (
        <Layer layer={2}>
            <Text
                color={"black"}
                fontSize={.2}
                position={[0, -(detailData.width / 2 * scale) - 0.65, 0]}
                maxWidth={6}
            >
                ← {detailData.length} →
            </Text>
            <Text
                rotation={[0, 0, 20.4]}
                color={"black"}
                fontSize={.2}
                position={[(detailData.length / 2 * scale) + 0.65, 0, 0]}
                maxWidth={6}
            >
                ← {detailData.width} →
            </Text>
        </Layer>
    )
})

const DetailProcessing = ({
                              setIsError,
                              detailId,
                              customerOrderNumber,
                              year,
                              setChange,
                              handleModalAlert,
                              orderId,
                              detailData,
                              redirect,
                              variables,
                              organization,
                              handleSubmitDetailModal,
                              isWarehouse,
                              loading,
                              model,
                              composition
                          }) => {
    const navigate = useNavigate();
    const refCanvas = useRef()
    const {ErrorBoundary, didCatch, error} = useErrorBoundary()
    const blockRedact = detailData.blockRedact || didCatch || isWarehouse
    const [scale, setScale] = useState(INIT_SCALE);
    const [zoomMultiplier, setZoomMultiplier] = useState(DETAIL_MIN_ZOOM_MULTIPLIER);
    const [viewPan, setViewPan] = useState({x: 0, y: 0});
    const [edgesData, setEdgesData] = useState([])
    const [usedEdges, setUsedEdges] = useState([])
    const usedEdgesIds = new Set(detailData?.edges.map((el) => el.orderEdge.id))

    useEffect(() => {
        const api = model ? OrderEdgeControllerOrganization.getForModel() :
            composition ? OrderEdgeControllerOrganization.getForComposition() :
                organization ? OrderEdgeControllerOrganization.getById(detailData.orderId) :
                    OrderEdgeController.getAll(orderId)
        api.then(function (response) {
            const data = response?.data
            setEdgesData(data);
            setUsedEdges(data.filter(el => usedEdgesIds.has(el.id)))
            setIsError(false)
        })
            .catch(function (error) {
                handleModalAlert(error)
            })
    }, []);

    useEffect(() => {
        const updateScale = () => setScale(calcNewScale(refCanvas.current || {}, detailData))
        updateScale()
        setZoomMultiplier(DETAIL_MIN_ZOOM_MULTIPLIER)
        setViewPan({x: 0, y: 0})

        const resizeHandler = () => updateScale()
        window.addEventListener("resize", resizeHandler)

        const canvasEl = refCanvas.current
        let resizeObserver
        if (canvasEl && typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(resizeHandler)
            resizeObserver.observe(canvasEl)
        }

        return () => {
            window.removeEventListener("resize", resizeHandler)
            resizeObserver?.disconnect()
        }
    }, [detailData])

    const startX = -(detailData.length / 2 * scale)
    const startY = -(detailData.width / 2 * scale)
    const corePosition = [startX, startY, 0];
    const {width: mapFitWidth, height: mapFitHeight} = calcProcessingFitSize(
        detailData.length,
        detailData.width,
        scale,
    );
    // Pan bounds are larger so LMB drag has room to move
    const mapPanWidth = mapFitWidth * 4;
    const mapPanHeight = mapFitHeight * 4;

    const [processingModal, setProcessingModal] = useState("")
    const [confirmModal, setConfirmModal] = useState("");
    const [popoverActives, setPopoverActives] = useState({pointId: null, x: 0, y: 0});
    const [editDetailModal, setEditDetailModal] = useState(false)

    const [editableElementId, setEditableElementId] = useState(null);
    const [activePointId, setActivePointId] = useState(null)
    const [selectedHole, setSelectedHole] = useState(null)
    const [hoveredHole, setHoveredHole] = useState(null)
    const [hoveredGroove, setHoveredGroove] = useState(null)
    const [prevArc, setPrevArc] = useState(null)
    const [prevInnerContour, setPrevInnerContour] = useState({contour: {}, type: ""})


    const cutPossible = useMemo(() => {
        const possibleArr = []

        orderContourPoints(detailData.detailContour?.points ?? []).forEach((el, i, arr) => {
            const possible = calcCutPossible(el, i, arr)
            possible && possibleArr.push({...possible, mainContour: true})
        })
        detailData.notches?.forEach((notch) => orderContourPoints(notch.contour?.points ?? []).forEach((el, i, arr) => {
            const possible = calcCutPossible(el, i, arr)
            possible && possibleArr.push({...possible, mainContour: false})
        }))
        return possibleArr
    }, [detailData])

    const [cutData, setCutData] = useState({
        pointId: null,
        possible: cutPossible,
        distancePrev: "",
        distanceNext: ""
    })


    const handleCloseDetailModal = (data) => {
        setEditDetailModal(false)
    }

    // detail-model / composition are organization pages even when orderId is missing (defaults to 0)
    const useOrganizationApi = Boolean(organization || model || composition)

    const calcPrevInnerContour = (enteredValues, position, round, pointForQuarter) => {
        const fetchPreview = () => useOrganizationApi
            ? VariableControllerServiceOrganization.prevAll(detailId, enteredValues)
            : VariableControllerService.prevAll(detailId, enteredValues, orderId)
        if (pointForQuarter) {
            fetchPreview().then(function (response) {
                const calcData = response.data

                const {
                    x,
                    y,
                    horizontal
                } = calcPrevQuarterXY(pointForQuarter, calcData.distToStartPoint, calcData.width, calcData.length, detailData)

                setPrevInnerContour({
                    contour: {
                        position: position,
                        width: horizontal ? calcData.length : calcData.width,
                        length: horizontal ? calcData.width : calcData.length,
                        x: x,
                        y: y,
                        distToStartPoint: calcData.distToStartPoint,
                    },
                    type: "groove"
                })
            })
                .catch(function (error) {
                    handleModalAlert(error)
                    setPrevInnerContour({contour: {}, type: ""})
                })
        } else {
            try {
                const calcValues = Object.fromEntries(Object.entries(enteredValues).map(([name, value]) => {
                    return [name, calcValueOfStringWithVar(variables, value.toString())]
                }))
                setPrevInnerContour({contour: {position: position, ...calcValues}, type: round ? "round" : "groove"})
            } catch (error) {
                fetchPreview().then(function (response) {
                    setPrevInnerContour({
                        contour: {position: position, ...response.data},
                        type: round ? "round" : "groove"
                    })
                })
                    .catch(function (error) {
                        handleModalAlert(error)
                        setPrevInnerContour({contour: {}, type: ""})
                    })
            }
        }
    }

    const processingHandle = (type, editId, pointId) => {
        type === "cut" && setCutData((value) => {
            return {...value, pointId: editId}
        })
        setEditableElementId(editId)
        setActivePointId(pointId)
        setProcessingModal(type)
    }

    const openConfirmModal = (type, deleteId) => {
        setConfirmModal(type)
        setEditableElementId(deleteId)
    }

    const openPopoverActives = (data) => {
        setPopoverActives(data)
        setActivePointId(data.pointId)
    }
    const closePopoverActives = () => {
        setPopoverActives({pointId: null, x: 0, y: 0})
        setActivePointId(null)
    }

    const onCloseModal = () => {
        setProcessingModal("")
        setEditableElementId(null)
        setPrevArc(null)
        setCutData(values => ({...values, distancePrev: "", distanceNext: ""}))
        setPrevInnerContour({contour: {}, type: ""})
        setActivePointId(null)
    }

    const onCloseConfirmModal = () => {
        setConfirmModal("")
    }

    const onConfirm = () => {
        if (confirmModal === "reset") {
            const api = organization ? DetailReserveService.resetContour(detailData.id) : DetailControllerService.resetContour(orderId, detailData.id)
            api.then(function () {
                setChange((change) => !change)
                onCloseConfirmModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (confirmModal === "deletePoint") {
            const api = organization ? PointControllerServiceOrganization.delete(editableElementId) : PointControllerService.delete(orderId, detailData.id, editableElementId)
            api.then(function () {
                setChange((change) => !change)
                onCloseConfirmModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (confirmModal === "deleteHole") {
            const api = organization ? HoleControllerServiceOrganization.delete(editableElementId) : HoleControllerService.delete(orderId, detailData.id, editableElementId)
            api.then(function () {
                setChange((change) => !change)
                onCloseConfirmModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (confirmModal === "deleteNotchRound") {
            const api = organization ? NotchControllerServiceOrganization.deleteRound(editableElementId) : NotchControllerService.deleteRound(orderId, detailData.id, editableElementId)
            api.then(function () {
                setChange((change) => !change)
                onCloseConfirmModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (confirmModal === "deleteGroove") {
            const api = organization ? GrooveControllerServiceOrganization.delete(editableElementId) : GrooveControllerService.delete(orderId, detailData.id, editableElementId)
            api.then(function () {
                setChange((change) => !change)
                onCloseConfirmModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (confirmModal === "deleteEdge") {
            const api = organization ? EdgesControllerServiceOrganization.delete(editableElementId) : EdgesControllerService.delete(orderId, editableElementId)
            api.then(function () {
                setChange((change) => !change)
                onCloseConfirmModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (confirmModal === "deleteQuarter") {
            const api = useOrganizationApi ? QuarterControllerServiceOrganization.delete(editableElementId) : QuarterControllerService.delete(orderId, detailId, editableElementId)
            api.then(function () {
                setChange((change) => !change)
                onCloseConfirmModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (confirmModal === "deleteNotch") {
            const api = organization ? NotchControllerServiceOrganization.delete(editableElementId) : NotchControllerService.delete(orderId, detailData.id, editableElementId)
            api.then(function () {
                setChange((change) => !change)
                onCloseConfirmModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        }
    }

    const onSubmitHandle = (data, handleClose) => {
        if (processingModal === "addPoint") {
            const api = organization ? PointControllerServiceOrganization.save(data.afterPointId, data.point) : PointControllerService.save(orderId, detailId, data.afterPointId, data.point)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "editPoint") {
            const api = organization ? PointControllerServiceOrganization.patch(activePointId, data.point) : PointControllerService.patch(orderId, detailId, activePointId, data.point)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "arc") {
            const api = organization ? PointControllerServiceOrganization.patchArc(data.pointId, data.point) : PointControllerService.patchArc(orderId, detailId, data.pointId, data.point)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "round") {
            const api = organization ? PointControllerServiceOrganization.patchRound(data.pointId, data.point) : PointControllerService.patchRound(orderId, detailId, data.pointId, data.point)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "cut") {
            const api = organization ? PointControllerServiceOrganization.patchCut(data.pointId, data.params) : PointControllerService.patchCut(orderId, detailId, data.pointId, data.params)
            api.then(function (response) {
                setChange((change) => !change)
                handleClose()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "notch") {
            const api = organization ? NotchControllerServiceOrganization.add(detailId, data) : NotchControllerService.add(orderId, detailId, data)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "editNotch") {
            const api = organization ? NotchControllerServiceOrganization.put(editableElementId, data) : NotchControllerService.put(orderId, detailId, editableElementId, data)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "notchRound") {
            const api = organization ? NotchControllerServiceOrganization.addRound(detailId, data) : NotchControllerService.addRound(orderId, detailId, data)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "editNotchRound") {
            const api = organization ? NotchControllerServiceOrganization.putRound(editableElementId, data) : NotchControllerService.putRound(orderId, detailId, editableElementId, data)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "addHole") {
            const api = organization ? HoleControllerServiceOrganization.save(detailId, data) : HoleControllerService.save(orderId, detailId, data)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "editHole") {
            const api = organization ? HoleControllerServiceOrganization.put(editableElementId, data) : HoleControllerService.put(orderId, detailId, editableElementId, data)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "addEdge") {
            const api = organization ? EdgesControllerServiceOrganization.save({
                startPointId: editableElementId,
                orderEdgeId: data,
                edgeEnd: "STANDARD"
            }) : EdgesControllerService.save(orderId, {
                startPointId: editableElementId,
                orderEdgeId: data,
                edgeEnd: "STANDARD"
            })
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "editEdge") {
            const api = organization ? EdgesControllerServiceOrganization.edit(editableElementId, {
                orderEdgeId: data,
                edgeEnd: "STANDARD"
            }) : EdgesControllerService.edit(orderId, editableElementId, {
                orderEdgeId: data,
                edgeEnd: "STANDARD"
            })
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "groove") {
            const api = organization ? GrooveControllerServiceOrganization.add(detailId, data) : GrooveControllerService.add(orderId, detailId, data)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "editGroove") {
            const api = organization ? GrooveControllerServiceOrganization.put(editableElementId, data) : GrooveControllerService.put(orderId, detailId, editableElementId, data)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "quarter") {
            const api = useOrganizationApi
                ? QuarterControllerServiceOrganization.add(detailId, data.pointId, data.params)
                : QuarterControllerService.add(orderId, detailId, data.pointId, data.params)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        } else if (processingModal === "editQuarter") {
            const api = useOrganizationApi
                ? QuarterControllerServiceOrganization.put(editableElementId, data.pointId, data.params)
                : QuarterControllerService.put(orderId, detailId, editableElementId, data.pointId, data.params)
            api.then(function (response) {
                setChange((change) => !change)
                onCloseModal()
            })
                .catch(function (error) {
                    handleModalAlert(error)
                })
        }
    }

    const getPrevContour = (data) => {
        if (!data.params.radius) {
            setPrevArc(null)
        } else if (processingModal === "arc") {
            const api = organization ? PointControllerServiceOrganization.previewArc(data.pointId, data.params) : PointControllerService.previewArc(orderId, detailId, data.pointId, data.params)
            api.then((response) => setPrevArc(response.data))
                .catch((err) => {
                    setPrevArc(null)
                    handleModalAlert(err)
                })
        } else {
            const api = organization ? PointControllerServiceOrganization.previewRound(data.pointId, data.params) : PointControllerService.previewRound(orderId, detailId, data.pointId, data.params)
            api.then((response) => setPrevArc(response.data))
                .catch((err) => {
                    setPrevArc(null)
                    handleModalAlert(err)
                })
        }
    }
    const redirectEdges = () => {
        if (orderId === 0) {
            navigate("/customer/samples/edges");
        } else if (organization) {
            const pathname = "/settings"
            EditLocalStoragePagesData(pathname, "settingName", "edgeReserveSettings")
            EditLocalStoragePagesData(pathname, "expanded", ["settings", "reserveSettings", "edgeReserveSettings"])
            navigate(pathname);
        } else {
            const pathname = `/customer/orders/${orderId}/${customerOrderNumber}/${year}`
            EditLocalStoragePagesData(pathname, "tab", 4)
            navigate(pathname);
        }
    }
    return (<Box>
        <ModalAddDetail
            open={editDetailModal}
            onClose={handleCloseDetailModal}
            title={'Редактировать деталь'}
            labelButtonSubmit={'Обновить'}
            handleSubmit={(data) => handleSubmitDetailModal(data, setEditDetailModal, detailData.id, orderId)}
            initValue={{
                position: detailData.position,
                name: detailData.name,
                length: detailData.length,
                height: "18",
                width: detailData.width,
                quantity: detailData.quantity
            }}
            organization={organization}
            uniqueNames={[]}
            loading={loading}
        />
        <ModalProcessing
            type={processingModal}
            onClose={onCloseModal}
            onSubmit={onSubmitHandle}
            edgesData={edgesData}
            detailData={detailData}
            editableElementId={editableElementId}
            activePointId={activePointId}
            setActivePointId={setActivePointId}
            openConfirmModal={openConfirmModal}
            cutData={cutData}
            setCutData={setCutData}
            calcPrevInnerContour={calcPrevInnerContour}
            getPrevContour={getPrevContour}
            refCanvas={refCanvas}
            variables={variables}
            redirectEdges={redirectEdges}
        />
        <ModalConfirm
            confirmModal={confirmModal}
            onClose={onCloseConfirmModal}
            onSubmit={onConfirm}
        />
        {popoverActives.pointId &&
            <PopoverProcessing popoverActives={popoverActives}
                               processingHandle={processingHandle}
                               openConfirmModal={openConfirmModal}
                               confirmModal={confirmModal}
                               processingModal={processingModal}
                               cutData={cutData}
                               onClose={closePopoverActives}
                               detailData={detailData}>
            </PopoverProcessing>
        }

        {detailData.id ? <Box sx={{display: "flex", flexDirection: "column", gap: "16px",}}>
            <Box sx={{display: "flex", justifyContent: "space-between"}}>
                <Box sx={{display: "flex", flexDirection: "column", gap: "16px",}}>
                    <Typography component="p" sx={{fontSize: "24px", fontWeight: 700}}>
                        {`Деталь ${detailData.position}`}
                    </Typography>
                    <Box sx={{display: "flex", gap: "8px"}}>
                        <Typography sx={{fontSize: "16px"}}>
                            Размер
                        </Typography>
                        <Typography sx={{fontSize: "16px"}}>
                            {`${detailData.length} мм * ${detailData.width} мм * ${detailData.height} мм`}
                        </Typography>
                        {!blockRedact &&
                            <PencilIcon stroke='#3B8BEC' onClick={() => setEditDetailModal(true)}/>}
                    </Box>
                </Box>
                <Box style={{display: "flex", gap: "16px", marginBottom: 10}}>
                    {!blockRedact && <ButtonBack
                        width={213} height={40} toStartContour={true} text={"К начальному контуру"}
                        onClick={() => openConfirmModal("reset")}>
                    </ButtonBack>}
                </Box>

            </Box>

            <Box style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "stretch",
                height: "calc(100vh - 410px)",
                gap: "16px",
            }}>
                <SideBar detailData={detailData} processingHandle={processingHandle} cutPossible={cutPossible}
                         reaOnly={blockRedact}/>
                <Box style={{
                    minWidth: "500px",
                    width: "calc(100% - 840px)",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    margin: "0 4px",
                }}>
                    <Box
                        ref={refCanvas}
                        sx={{
                            minWidth: "500px",
                            height: "100%",
                            display: "flex",
                            outline: (theme) => `${theme.palette.primary.stroke} 1px solid`,
                            borderRadius: "4px",
                            overflow: "hidden",
                        }}>
                        {didCatch ? (
                            <Box sx={{display: "flex", justifyContent: "center", width: "100%", paddingTop: "230px"}}>
                                <Typography
                                    sx={{
                                        fontSize: "20px",
                                        fontWeight: 700,
                                        color: (theme) => theme.palette.error.light
                                    }}>Ваших
                                    системных ресурсов не достаточно для отрисовки детали</Typography>
                            </Box>
                        ) : (
                            <ErrorBoundary>
                                <DetailViewTransformContext.Provider value={viewPan}>
                                    <Canvas
                                        style={{width: "100%", height: "100%"}}
                                        raycaster={{params: {Line: {threshold: 0.15}, Mesh: {}, Points: {threshold: 0.15}}}}
                                    >
                                        <DetailMapControls
                                            enabled={true}
                                            zoomMultiplier={zoomMultiplier}
                                            onZoomChange={setZoomMultiplier}
                                            fitWidth={mapFitWidth}
                                            fitHeight={mapFitHeight}
                                            panWidth={mapPanWidth}
                                            panHeight={mapPanHeight}
                                            pan={viewPan}
                                            onPanChange={setViewPan}
                                        />
                                        <MainDimension detailData={detailData} scale={scale}/>

                                        <DetailCanvas
                                            editableElement={{
                                                type: processingModal,
                                                pointId: activePointId,
                                                id: editableElementId
                                            }}
                                            processingHandle={processingHandle}
                                            hoveredHoleId={hoveredHole}
                                            setHoveredHoleId={setHoveredHole}
                                            selectedHole={selectedHole}
                                            setSelectedHole={setSelectedHole}
                                            hoveredGrooveId={hoveredGroove}
                                            setHoveredGrooveId={setHoveredGroove}
                                            cutData={cutData}
                                            prevInnerContour={prevInnerContour}
                                            prevArc={prevArc}
                                            openPopoverActives={openPopoverActives}
                                            openConfirmModal={openConfirmModal}
                                            position={corePosition}
                                            detailData={detailData}
                                            variables={variables}
                                            scale={scale}
                                            reaOnly={blockRedact}
                                        />
                                        <Layer layer={1}>
                                            <arrowHelper
                                                args={[new THREE.Vector3(0, 0, 0), new THREE.Vector3(startX - 0.7, startY - 1, 0), 1, "#F87B0E", 0.2, 0.1]}
                                            />
                                            <arrowHelper
                                                args={[new THREE.Vector3(90, 0, 0), new THREE.Vector3(startX - 0.7, startY - 1, 0), 0.7, "#F87B0E", 0.2, 0.1]}
                                            />
                                            <Text
                                                color={"#F87B0E"}
                                                fontSize={.15}
                                                position={[startX + 0.1, startY - 0.994, 0]}>X
                                            </Text>
                                            <Text
                                                color={"#F87B0E"}
                                                fontSize={.15}
                                                position={[startX - 0.7, startY + 0.2, 0]}>Y
                                            </Text>
                                        </Layer>
                                        <ambientLight intensity={Math.PI / 2}/>
                                        <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} decay={0}
                                                   intensity={Math.PI}/>
                                        <pointLight position={[-10, -10, -10]} decay={0} intensity={Math.PI}/>
                                    </Canvas>
                                </DetailViewTransformContext.Provider>
                            </ErrorBoundary>
                        )}
                    </Box>

                </Box>
                <Box sx={{display: "flex", flexDirection: "column", gap: "24px", maxWidth: "850px"}}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: "16px"}}>
                        <Typography sx={{fontWeight: 700}}>Кромки</Typography>
                        <ProcessingEdgesTable
                            usedEdges={usedEdges}>
                        </ProcessingEdgesTable>
                    </Box>
                    <Box sx={{display: "flex", flexDirection: "column", gap: "16px"}}>
                        <Typography sx={{fontWeight: 700}}>Отверстия</Typography>
                        <ProcessingTable
                            data={detailData.holes}
                            processingHandle={processingHandle}
                            openConfirmModal={openConfirmModal}
                            hoveredHole={hoveredHole}
                            setHoveredHole={setHoveredHole}
                            reaOnly={blockRedact}
                            numberEdgesInTable={usedEdges.length}/>
                    </Box>
                </Box>
            </Box>
        </Box> : <p style={{color: "red"}}> the detail does not exist </p>}
    </Box>)
}
export default DetailProcessing
